/**
 * Relatório da aula em formato de documento, para impressão e PDF.
 *
 * Não é a tela com abas: ali o usuário navega, aqui tudo precisa estar na
 * página ao mesmo tempo, porque um PDF não tem aba para clicar.
 *
 * Fica oculto em tela (`hidden print:block`) e só aparece na impressão. O
 * navegador gera o PDF, então o que se vê no preview é o que sai no arquivo
 * — sem biblioteca de PDF e sem um segundo layout para manter em sincronia.
 */
import { useLessonInsights } from '../application/insights';
import { DEVICE_LABELS } from '../domain/engagement';
import {
  clock,
  longDayLabel,
  PERIODS,
  watchTime,
  WEEKDAYS,
  type LessonInsights,
  type PeriodDays,
} from '../domain/insights';

interface Props {
  lessonId: string;
  days: PeriodDays;
  /** Vêm por props: `learning` não importa `catalog` (fronteira do ESLint). */
  lessonTitle: string;
  moduleTitle: string;
  courseTitle: string;
  durationS: number;
}

const pt = (n: number) => n.toLocaleString('pt-BR');

export function LessonReport({ lessonId, days, lessonTitle, moduleTitle, courseTitle, durationS }: Props) {
  // Mesma query da tela: o React Query reaproveita o cache, sem segunda ida à API.
  const { data } = useLessonInsights(lessonId, days);
  if (!data) return null;

  const period = PERIODS.find((p) => p.days === days)?.label ?? 'Período';
  const generated = new Date(data.generated_at).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <article className="hidden print:block">
      <header className="mb-6 border-b border-line pb-4">
        <p className="text-xs tracking-wider text-fg-subtle uppercase">Relatório da aula</p>
        <h1 className="mt-1 text-2xl font-bold">{lessonTitle}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {courseTitle} · {moduleTitle} · {clock(durationS)}
        </p>
        <p className="mt-2 text-xs text-fg-subtle">
          {period} · gerado em {generated}
        </p>
      </header>

      <Section title="Alcance">
        <Grid>
          <Stat label="Visualizações" value={pt(data.totals.views)} />
          <Stat label="Espectadores únicos" value={pt(data.totals.viewers)} />
          <Stat label="Tempo de exibição" value={watchTime(data.totals.watched_s)} />
          <Stat label="Duração média" value={watchTime(data.totals.avg_view_duration_s)} />
          <Stat label="Média assistida" value={`${pt(data.totals.avg_percent_viewed)}%`} />
          <Stat label="Concluíram" value={`${Math.round(data.totals.completion_rate * 100)}%`} />
        </Grid>
      </Section>

      <Section title="Engajamento">
        <Grid>
          <Stat label="Curtidas" value={pt(data.totals.likes)} />
          <Stat
            label="Avaliação"
            value={
              data.ratings.count
                ? data.ratings.average.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                : '—'
            }
            hint={data.ratings.count ? `${pt(data.ratings.count)} notas` : 'sem notas'}
          />
          <Stat label="Downloads" value={pt(data.downloads)} hint="materiais da aula" />
          <Stat label="Velocidade média" value={`${pt(data.totals.avg_speed)}×`} />
        </Grid>
        {data.ratings.count > 0 && <RatingBars ratings={data.ratings} />}
      </Section>

      {data.professions.length > 0 && (
        <Section title="Profissão" subtitle="Um grupo pode ser minoria e ainda assim consumir mais">
          <SegmentTable items={data.professions} first="Profissão" />
        </Section>
      )}

      {data.sources.length > 0 && (
        <Section title="Origem" subtitle="Como a pessoa chegou à plataforma">
          <SegmentTable items={data.sources} first="Origem" />
        </Section>
      )}

      {data.drops.length > 0 && (
        <Section title="Principais quedas" subtitle="Onde mais gente parou de assistir">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-fg-subtle uppercase">
                <th className="py-1.5">Trecho</th>
                <th className="py-1.5 text-right">Queda</th>
              </tr>
            </thead>
            <tbody>
              {data.drops.map((d) => (
                <tr key={d.from_s} className="border-b border-line/60">
                  <td className="py-1.5 tabular-nums">
                    {clock(d.from_s)} → {clock(d.to_s)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">−{pt(d.drop)} p.p.</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      <Section title="Dispositivos e qualidade">
        <div className="grid grid-cols-2 gap-6">
          <ShareTable
            title="Dispositivos"
            items={data.devices.map((d) => ({
              ...d,
              key: DEVICE_LABELS[d.key as keyof typeof DEVICE_LABELS] ?? d.key,
            }))}
          />
          <ShareTable title="Qualidade" items={data.qualities} />
        </div>
      </Section>

      <Section title="Quando assistem">
        <div className="grid grid-cols-2 gap-6">
          <ShareTable
            title="Dias da semana"
            items={data.weekdays.map((v, i) => ({
              key: WEEKDAYS[i]!,
              views: v,
              percent: data.totals.views ? Math.round((v / data.totals.views) * 1000) / 10 : 0,
            }))}
          />
          <ShareTable
            title="Horários com mais visualizações"
            items={data.hours
              .map((v, h) => ({
                key: `${h}h`,
                views: v,
                percent: data.totals.views ? Math.round((v / data.totals.views) * 1000) / 10 : 0,
              }))
              .filter((h) => h.views > 0)
              .sort((a, b) => b.views - a.views)
              .slice(0, 6)}
          />
        </div>
      </Section>

      {data.daily.length > 0 && (
        <Section title="Dia a dia">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-fg-subtle uppercase">
                <th className="py-1.5">Dia</th>
                <th className="py-1.5 text-right">Visualizações</th>
                <th className="py-1.5 text-right">Espectadores</th>
                <th className="py-1.5 text-right">Tempo</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.map((d) => (
                <tr key={d.date} className="border-b border-line/60">
                  <td className="py-1.5">{longDayLabel(d.date)}</td>
                  <td className="py-1.5 text-right tabular-nums">{pt(d.views)}</td>
                  <td className="py-1.5 text-right tabular-nums">{pt(d.viewers)}</td>
                  <td className="py-1.5 text-right tabular-nums">{watchTime(d.watched_s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </article>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  // `break-inside-avoid` evita que um bloco fique partido entre páginas.
  return (
    <section className="mb-6 break-inside-avoid">
      <h2 className="mb-1 text-base font-bold">{title}</h2>
      {subtitle && <p className="mb-2 text-xs text-fg-subtle">{subtitle}</p>}
      {children}
    </section>
  );
}

const Grid = ({ children }: { children: React.ReactNode }) => (
  <dl className="grid grid-cols-3 gap-4">{children}</dl>
);

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-line p-3">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="mt-0.5 text-lg font-bold tabular-nums">{value}</dd>
      {hint && <p className="text-xs text-fg-subtle">{hint}</p>}
    </div>
  );
}

function RatingBars({ ratings }: { ratings: LessonInsights['ratings'] }) {
  return (
    <div className="mt-3 space-y-1">
      {[5, 4, 3, 2, 1].map((n) => {
        const qty = ratings.distribution[n] ?? 0;
        const pct = ratings.count ? Math.round((qty / ratings.count) * 100) : 0;
        return (
          <div key={n} className="flex items-center gap-2 text-xs">
            <span className="w-8 tabular-nums text-fg-subtle">{n} ★</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
              <div className="h-full rounded-full bg-brand-400" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-16 text-right tabular-nums text-fg-subtle">
              {pt(qty)} ({pct}%)
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SegmentTable({ items, first }: { items: LessonInsights['professions']; first: string }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-line text-left text-xs text-fg-subtle uppercase">
          <th className="py-1.5">{first}</th>
          <th className="py-1.5 text-right">Pessoas</th>
          <th className="py-1.5 text-right">Tempo total</th>
          <th className="py-1.5 text-right">Por pessoa</th>
        </tr>
      </thead>
      <tbody>
        {items.map((s) => (
          <tr key={s.key} className="border-b border-line/60">
            <td className="py-1.5">{s.label}</td>
            <td className="py-1.5 text-right tabular-nums">{pt(s.people)}</td>
            <td className="py-1.5 text-right tabular-nums">{watchTime(s.watched_s)}</td>
            <td className="py-1.5 text-right tabular-nums">{watchTime(s.avg_per_person_s)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ShareTable({
  title,
  items,
}: {
  title: string;
  items: { key: string; views: number; percent: number }[];
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium tracking-wider text-fg-subtle uppercase">{title}</h3>
      <table className="w-full text-sm">
        <tbody>
          {items.map((s) => (
            <tr key={s.key} className="border-b border-line/60">
              <td className="py-1">{s.key}</td>
              <td className="py-1 text-right tabular-nums">{pt(s.views)}</td>
              <td className="w-14 py-1 text-right tabular-nums text-fg-subtle">{pt(s.percent)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
